/*
 * AuthContext.tsx
 * Contexte React gérant les droits de l'utilisateur courant.
 *
 * Charge les permissions une seule fois au montage (via GET /api/v2/me/permissions)
 * et les expose à toute l'application via le hook useAuth().
 *
 * Utilisation :
 *   const { isAdmin, canRead, canWrite, canAll } = useAuth();
 *   if (canRead('users')) { ... }
 *   if (canAll([{ module: 'badge', action: 'write' }, { module: 'activites', action: 'read' }])) { ... }
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import apiFetch from 'common/services/apiFetch';
import { getApiBaseUrl } from 'common/services/apiBaseUrl';
import { isCurrentUserAdmin } from 'common/services/authUtils';

// Entrée de permission telle que retournée par GET /api/v2/me/permissions
interface PermissionEntry {
	module: string;
	action: string;
}

// Structure retournée par GET /api/v2/me/permissions
interface MePermissionsResponse {
	isAdmin: boolean;
	permissions: PermissionEntry[];
}

// Condition pour canAll()
export interface PermissionCheck {
	module: string;
	/** Défaut : 'read' si omis */
	action?: string;
}

// Valeurs exposées par le contexte
interface AuthContextValue {
	/** true si l'utilisateur est ROLE_ADMIN */
	isAdmin: boolean;
	/** true si les permissions ont fini de se charger */
	permissionsLoaded: boolean;
	/** Retourne true si l'utilisateur peut lire le module donné */
	canRead: (moduleName: string) => boolean;
	/** Retourne true si l'utilisateur peut écrire dans le module donné */
	canWrite: (moduleName: string) => boolean;
	/**
	 * Retourne true si l'utilisateur satisfait TOUTES les conditions données (AND).
	 * Chaque condition spécifie un module et une action (défaut: 'read').
	 */
	canAll: (checks: PermissionCheck[]) => boolean;
}

const AuthContext = createContext<AuthContextValue>({
	isAdmin: false,
	permissionsLoaded: false,
	canRead: () => false,
	canWrite: () => false,
	canAll: () => false,
});

/**
 * Provider à placer dans App.tsx autour du Layout protégé.
 * Charge les permissions dès le montage si un token est présent.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [isAdmin, setIsAdmin] = useState<boolean>(false);
	const [permissions, setPermissions] = useState<PermissionEntry[]>([]);
	const [permissionsLoaded, setPermissionsLoaded] = useState<boolean>(false);

	useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) {
			setPermissionsLoaded(true);
			return;
		}

		const apiBaseUrl = getApiBaseUrl();

		apiFetch(`${apiBaseUrl}/v2/me/permissions`, {
			headers: {
				Authorization: `Bearer ${token}`,
				'Content-Type': 'application/json',
			},
		})
			.then(async (res) => {
				if (!res.ok) return;
				const data: MePermissionsResponse = await res.json();
				setIsAdmin(data.isAdmin);
				setPermissions(data.permissions ?? []);
			})
			.catch(() => {
				// Fallback : utiliser le rôle local depuis le JWT en cas d'erreur réseau
				const adminFromJwt = isCurrentUserAdmin();
				setIsAdmin(adminFromJwt);
			})
			.finally(() => setPermissionsLoaded(true));
	}, []);

	/** Vérifie si l'utilisateur a la permission donnée sur le module donné */
	const can = (moduleName: string, action: string): boolean => {
		if (isAdmin) return true;
		return permissions.some(
			(p) => p.module === moduleName && p.action === action
		);
	};

	const canRead = (moduleName: string): boolean => can(moduleName, 'read');
	const canWrite = (moduleName: string): boolean => can(moduleName, 'write');

	const canAll = (checks: PermissionCheck[]): boolean =>
		checks.every((c) => can(c.module, c.action ?? 'read'));

	return (
		<AuthContext.Provider
			value={{ isAdmin, permissionsLoaded, canRead, canWrite, canAll }}
		>
			{children}
		</AuthContext.Provider>
	);
};

/**
 * Hook pour accéder aux droits de l'utilisateur courant dans n'importe quel composant.
 */
export const useAuth = (): AuthContextValue => useContext(AuthContext);
